<template>
    <div>
        <b-form novalidate>
            <b-form-group v-if="inputLimits">
                <template slot="label">
                    <b-row>
                        <b-col cols="9">{{ $t('project.settings.general.name') }}: *</b-col>
                        <b-col cols="3" class="text-right">
                            <small>
                                <b-badge>{{projectData.name.length}} / {{inputLimits.projectName}}</b-badge>
                            </small>
                        </b-col>
                    </b-row>
                </template>
                <b-form-input
                    type="text"
                    name="projectName"
                    v-validate="'required'"
                    v-model="projectData.name"
                    :maxlength="inputLimits.projectName"
                    :disabled="!projectPermissions.includes('CHANGE_PROJECT_ROOM_NAME')"
                    :placeholder="$t('project.settings.general.namePlaceholder')"
                    :state="!veeErrors.has('projectName')">
                </b-form-input>
                <span v-show="veeErrors.has('projectName')" style="color: red;" class="small">{{ $t('project.settings.general.validation.projectName') }}</span>
            </b-form-group>
            <b-form-group v-if="inputLimits">
                <template slot="label">
                    <b-row>
                        <b-col cols="9">{{ $t('project.settings.general.description') }}:</b-col>
                        <b-col cols="3" class="text-right">
                            <small>
                                <b-badge>{{projectData.description.length}} / {{inputLimits.projectDescription}}</b-badge>
                            </small>
                        </b-col>
                    </b-row>
                </template>
                <b-form-textarea
                    no-resize
                    v-model="projectData.description"
                    :rows="3"
                    :max-rows="5"
                    :maxlength="inputLimits.projectDescription"
                    :disabled="!projectPermissions.includes('CHANGE_PROJECT_ROOM_DESCRIPTION')"
                    :placeholder="$t('project.settings.general.descriptionPlaceholder')">
                </b-form-textarea>
            </b-form-group>
            <b-form-group v-if="!deletedRoom">
                <label><strong>{{ $t('project.settings.general.rootObject') }}</strong></label>
                <a v-if="rawData"
                    :href="rawData.id ? (`/colab/epr/documents/link/${rawData.id}`) : 'javascript:void(0)'">
                    <span class="ml-2">({{ $t('project.settings.general.downloadRootObject') }})</span>
                </a>
                <div class="form-control pt-3 bg-light wrapper">
                    <div class="left">
                        <icon name="folder" scale="3" v-if="this.rawData && this.rawData.type && this.rawData.type.toUpperCase() === 'REGISTER'"></icon>
                        <icon name="folder-open" scale="3" v-else></icon>
                    </div>
                    <div class="right">
                        <span><strong>{{ rawData ? rawData.title : '' }}</strong></span>
                        <p>{{ rawData ? rawData.info : '' }} (<em>{{ rawData ? rawData.id : '' }}</em>)</p>
                    </div>
                </div>
            </b-form-group>
            <b-form-group v-if="!deletedRoom">
                <label><strong>{{ $t('project.settings.general.uploadObjectType') }}</strong></label>
                <b-alert v-if="isColabTypeAvailable === null" show>
                    <h5 class="m-0">{{ $t('project.settings.general.waitingUploadStatus') }}</h5>
                </b-alert>
                <b-alert v-else-if="isColabTypeAvailable" show variant="success">
                    <b-media no-body>
                        <b-media-aside vertical-align="center">
                            <icon name="check-circle" scale="3"></icon>
                        </b-media-aside>
                        <b-media-body class="ml-3">
                            <h5 class="m-0 pt-3 pb-3">{{ $t('project.settings.general.colabTypeAvailable') }}</h5>
                        </b-media-body>
                    </b-media>
                </b-alert>
                <b-alert v-else-if="!isColabTypeAvailable" show variant="warning">
                    <b-media no-body>
                        <b-media-aside vertical-align="center">
                            <icon name="warning" scale="3"></icon>
                        </b-media-aside>
                        <b-media-body class="ml-3">
                            <h5 class="m-0">{{ $t('project.settings.general.noColabTypeAvailable') }}</h5>
                            <p class="m-0">{{ $t('project.settings.general.contactAdmin') }}</p>
                        </b-media-body>
                    </b-media>
                </b-alert>
            </b-form-group>
            <b-form novalidate v-if="!deletedRoom && isProjectOwner">
                <label><strong>{{ $t('project.settings.general.showOrHide') }}</strong></label>
                <b-form-group>
                    <b-form-checkbox v-model="projectData.showMessages">
                        <span class="line-h-25" v-text="$t('project.settings.general.showMessages')"></span>
                    </b-form-checkbox>
                    <b-form-checkbox v-model="projectData.showTasks">
                        <span class="line-h-25" v-text="$t('project.settings.general.showTasks')"></span>
                    </b-form-checkbox>
                </b-form-group>
            </b-form>
            <b-form-group v-else-if="deletedRoom">
                <label><strong>{{ $t('project.settings.general.folderOrRegisterNotAvailable') }}</strong></label>
                <b-alert show variant="warning">
                    <b-media no-body>
                        <b-media-aside vertical-align="center">
                            <icon name="warning" scale="3"></icon>
                        </b-media-aside>
                        <b-media-body class="ml-3">
                            <p class="m-0">{{ $t('project.settings.general.noGeneralDeletedRoom') }}</p>
                        </b-media-body>
                    </b-media>
                </b-alert>
            </b-form-group>
        </b-form>
        <hr>
        <b-button type="button" class="btn-primary" @click="saveSettings()" v-show="isProjectOwner">{{ $t('project.settings.apply') }}</b-button>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
    name: 'tab-general',

    data() {
        return {
            deletedRoom: false
        }
    },

    mounted() {
        this.loadRawRoot();
    },

    computed: {
        ...mapState('system', ['inputLimits']),
        ...mapGetters({
            isProjectOwner: 'projects/isProjectOwner',
            rawData: 'projects/enaioRootObject',
            projectData: 'projects/projectData',
            isColabTypeAvailable: 'projects/isColabTypeAvailable'
        }),
        projectPermissions() {
          if(this.$store.state.projects.currentProject.projectData.role){
            return this.$store.state.projects.currentProject.projectData.role.permissions;
          } else {
            return [];
          }
        },
    },

    methods: {
        loadRawRoot() {
            this.$store.dispatch('projects/loadRawRoot', this.$route.params.projectId)
                .then((response) => {
                    this.deletedRoom = false;
                })
                .catch((error) => {
                    if (error.response.data.type === 'FOLDER_OR_REGISTER_NOT_AVAILABLE') {
                        console.log('Error loading files', error.response.data.type);
                        this.deletedRoom = true;
                    }
                    console.log('Error loading files', error);
                });
        },

        saveSettings() {
            this.$validator.validateAll().then(result => {
                if (result) {
                    this.$store.dispatch('projects/updateProject', this.projectData);
                }
            });
        }
    }
}
</script>

<style scoped>
.wrapper {
  display: flex;
}

.left {
  flex: 0 0 10%;
}

.right {
  flex: 1;
}
</style>
